import imageCompression from 'browser-image-compression'
import { isPayloadTooLargeApiError, PayloadTooLargeError } from './api-error'

const IMAGE_MIME_PREFIX = 'image/'
// O proxy de producao rejeita corpos acima de ~1MB (413); o alvo de 0.7MB por
// requisicao deixa margem para os demais campos do multipart.
export const DEFAULT_MAX_SIZE_MB = 0.7
// Mesmo limite de largura aplicado pelo backend ao gerar o webp final.
const DEFAULT_MAX_WIDTH_OR_HEIGHT = 1920
// Guarda pre-envio: acima disso o proxy rejeitaria de qualquer forma.
const MAX_UPLOAD_BYTES = 0.9 * 1024 * 1024

// Chrome/Firefox reportam type vazio para HEIC, entao a extensao tambem conta.
const HEIC_MIME_TYPES = [
	'image/heic',
	'image/heif',
	'image/heic-sequence',
	'image/heif-sequence',
]
const HEIC_EXTENSION_REGEX = /\.hei[cf]$/i

function isHeicFile(file: File): boolean {
	return (
		HEIC_MIME_TYPES.includes(file.type) || HEIC_EXTENSION_REGEX.test(file.name)
	)
}

// O sharp do backend nao tem suporte a HEIF compilado; a conversao precisa
// acontecer no browser. heic2any (libheif via wasm) decodifica em qualquer
// browser — o canvas usado pela compressao so decodifica HEIC no Safari.
async function convertHeicToJpeg(file: File): Promise<File> {
	const { default: heic2any } = await import('heic2any')
	const result = await heic2any({
		blob: file,
		toType: 'image/jpeg',
		quality: 0.9,
	})
	const blob = Array.isArray(result) ? result[0] : result
	const name = `${file.name.replace(HEIC_EXTENSION_REGEX, '')}.jpg`
	return new File([blob], name, { type: 'image/jpeg' })
}

// Converte HEIC para JPEG no momento da selecao: <img> nao renderiza HEIC fora
// do Safari, entao o preview (createObjectURL) precisa do arquivo ja convertido.
export async function convertHeicIfNeeded(file: File): Promise<File> {
	if (!isHeicFile(file)) {
		return file
	}
	try {
		return await convertHeicToJpeg(file)
	} catch (error) {
		console.warn('Falha ao converter HEIC, enviando arquivo original', error)
		return file
	}
}

export async function optimizeImageForUpload(
	file: File,
	maxSizeMB: number = DEFAULT_MAX_SIZE_MB,
): Promise<File> {
	file = await convertHeicIfNeeded(file)

	if (!file.type.startsWith(IMAGE_MIME_PREFIX)) {
		return file
	}

	if (file.size <= maxSizeMB * 1024 * 1024) {
		return file
	}

	try {
		const compressedFile = await imageCompression(file, {
			maxSizeMB,
			maxWidthOrHeight: DEFAULT_MAX_WIDTH_OR_HEIGHT,
			initialQuality: 0.9,
			useWebWorker: true,
		})

		if (compressedFile.size >= file.size) {
			return file
		}

		return compressedFile
	} catch (error) {
		console.warn('Falha ao comprimir imagem, enviando arquivo original', error)
		return file
	}
}

// Falha antes de ir a rede quando a soma dos arquivos ja excede o limite do proxy
// (ex: compressao falhou silenciosamente com HEIC e devolveu o original).
export function assertUploadSizeWithinLimit(files: File[]): void {
	const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
	if (totalBytes > MAX_UPLOAD_BYTES) {
		throw new PayloadTooLargeError(totalBytes)
	}
}

// Envia com o orcamento padrao de compressao; se o servidor responder 413,
// recomprime com metade do orcamento e tenta uma unica vez de novo.
export async function sendWithCompressionRetry<T>(
	send: (maxSizeMB: number) => Promise<T>,
): Promise<T> {
	try {
		return await send(DEFAULT_MAX_SIZE_MB)
	} catch (error) {
		if (!isPayloadTooLargeApiError(error)) {
			throw error
		}
		return await send(DEFAULT_MAX_SIZE_MB / 2)
	}
}
